<template>
    <div class="card mb-0" v-if="simulations">

        <p class="card-body mt-2" v-if="empty">
            <strong>{{ $t('dashboard.no-simulation') }}</strong>
        </p>

        <div v-if="!empty">

            <el-table class="table-responsive table-flush"
                    :show-header="false"
                    :data="visibleSimulations">

                <el-table-column>
                    <template v-slot="{row}">

                        <span class="pin-icon" :style="{color: pinColor(row)}" @click.prevent="pinSimulation(row)">
                            <i class="fas fa-thumbtack"></i>
                        </span>
                        <h3 class="mb-0 pl-3">
                            <a href="#" @click.prevent="openSimulation(row)"
                                v-if="row.session && (row.state.published || $permissions.hasRight('dashboard.learners.write'))">
                                {{ row.name }}
                            </a>
                            <span v-else>
                                {{ row.name }}
                            </span>
                            <small v-if="row.remedial">
                                <i class="fas fa-undo ml-1"></i>
                            </small>
                            <i class="fas fa-clipboard-check ml-1" v-if="row.assessment"></i>
                        </h3>

                    </template>
                </el-table-column>

                <el-table-column width="130">
                    <template v-slot="{row}">

                        <badge type="success" v-if="row.state.published" class="mb-1">
                            <i class="fas fa-calendar-alt mr-1"></i>
                            {{ date(row) }}
                        </badge>

                        <small class="d-block"
                                v-if="row.state.published && registration.state.meta && registration.state.meta.go_ground == row.id">
                            <i class="fas fa-plane-departure mr-1" style="margin-left:14px"></i>
                            {{ $t('degrees.go-ground') }}
                        </small>

                    </template>
                </el-table-column>

                <el-table-column>
                    <template v-slot="{row}">

                        <small class="d-block"
                            v-if="row.state.published && position(row)"
                        >
                            <i class="fas fa-sync-alt mr-1"></i> {{ position(row) }}
                        </small>

                        <small class="d-block"
                            v-if="row.state.published && teammate(row) && $permissions.hasRight('dashboard.learners.read')"
                        >
                            <i class="fas fa-user-friends mr-1"></i> {{ teammate(row).name }}
                        </small>

                        <small v-if="row.state.published" class="d-block">
                            <i class="fas fa-pencil-alt mr-1"></i> {{ row.state.instructor_name }}
                        </small>

                        <small v-if="row.state.acknowledged" class="d-block">
                            <i class="fas fa-check mr-1"></i> {{ learner.name }}
                        </small>

                    </template>
                </el-table-column>

                <el-table-column align="right" class-name="actions">
                    <template v-slot="{row}">
                        <div class="pt-1 pr-1 pb-1">

                            <base-button type="secondary" size="sm" class="mb-1 mr-0" @click="openLog(row)"
                                v-if="!activeSession(row) && !row.remedial && !row.assessment && $permissions.hasRight('dashboard.passive-log.access')"
                            >
                                {{ $t('common.log') }}
                            </base-button>

                            <base-button type="warning" size="sm" class="mb-1 mr-0" @click="openSessionLog(row)"
                                v-if="activeSession(row) && !row.remedial && !row.assessment && !row.session.ended_at && $permissions.hasRight('dashboard.learners.write')"
                            >
                                <i class="fas fa-play mr-1"></i> {{ $t('common.log') }}
                            </base-button>

                            <base-button type="secondary" size="sm" class="mb-1 mr-0" @click="openSessionLog(row)"
                                v-if="activeSession(row) && !row.remedial && !row.assessment && row.session.ended_at && $permissions.hasRight('dashboard.learners.write')"
                            >
                                <i class="fas fa-play mr-1"></i> {{ $t('common.log') }}
                            </base-button>

                            <base-button type="primary" size="sm" class="mb-1 mr-0 ml-1"
                                @click="openSimulation(row)" v-if="row.state.published && canShowReview(row) && $permissions.hasRight('dashboard.sessions.read')"
                            >
                                {{ $t('dashboard.more') }}
                            </base-button>

                            <base-button type="secondary" size="sm" class="mb-1 mr-0 ml-1" @click="openSimulation(row)"
                                v-if="(row.session || row.assessment) && !row.state.published && $permissions.hasRight('dashboard.learners.write')"
                            >
                                <i class="fas fa-pencil-alt"></i> {{ $t('common.edit') }}
                            </base-button>

                        </div>
                    </template>
                </el-table-column>
                
            </el-table>
        </div>

        <portal to="modals">
            <comment-modal v-model="modals.comment" :comment="comment">
            </comment-modal>
        </portal>
    </div>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import CommentModal from './CommentModal'
    import moment from 'moment'

    export default {

        components: {
            CommentModal
        },

        props: {

            learner: {
                type: Object,
                required: true
            },

            registration: {
                type: Object,
            },

            simulations: {
                type: Array,
            }
        },
        
        data() {
            return {
                modals: new ModalsController('comment'),
                comment: { 
                    title: null,
                    content: null,
                    author: null
                },
                loaded: false,
                moment: moment,
                pinned: this.$auth.getUiConfig('pinned_simulation_in_registration_' + this.registration.id),
            }
        },

        computed: {
            empty() {
                return this.visibleSimulations.length == 0
            },

            visibleSimulations() {
                let visibleSimulations = this.simulations.filter(sim => !sim.private || this.$permissions.hasRight('dashboard.learners.write'))
                if (!this.pinned) {
                    return visibleSimulations
                }
                let visible = false;
                let pinnedSimulations = [];
                for (const simulation of visibleSimulations) {
                    if (simulation.id == this.pinned) {
                        visible = true;
                    }
                    if (visible) {
                        pinnedSimulations.push(simulation)
                    }
                }
                return pinnedSimulations
            }
        },

        methods: {

            pinColor(simulation) {
                return this.pinned == simulation.id ? '#32325d' : '#cccccc';
            },

            canShowReview(simulation) {
                if (this.$permissions.hasRight('dashboard.overviews.always')) {
                    return true
                }
                if (simulation.private) {
                    return false
                }
                if (this.$permissions.hasRight('dashboard.overviews.closed')) {
                    return this.simulationClosed(simulation)
                }
                if (this.$permissions.hasRight('dashboard.overviews.date-or-closed')) {
                    if (simulation.assessment) {
                        return this.simulationAfterDate(simulation)
                    } else {
                        return this.simulationClosed(simulation)
                    }
                }
                return false
            },

            simulationClosed(simulation) {
                return simulation.state.date
                    && new Date(simulation.state.date) <= new Date()
            },

            simulationAfterDate(simulation) {
                return simulation.state.meta
                    && simulation.state.meta.learner_access_date
                    && new Date(simulation.state.meta.learner_access_date) <= new Date()
            },

            activeSession(simulation) {
                return simulation.session
                    && !simulation.state.published
            },

            date(simulation) {
                if (simulation.session && simulation.session.started_at) {
                    return this.moment(simulation.session.started_at).format('YYYY/MM/DD')
                } else {
                    return this.moment(simulation.state.date).format('YYYY/MM/DD')
                }
            },
            
            teammate(simulation) {
                if (!simulation.session) {
                    return false
                }
                if (this.learner.id == simulation.session.learner1_id && simulation.session.learner2) {
                    return simulation.session.learner2
                }
                if (this.learner.id == simulation.session.learner2_id && simulation.session.learner1) {
                    return simulation.session.learner1
                }
                if (simulation.session.teammate) {
                    return simulation.session.teammate
                }
                return null
            },

            position(simulation) {
                if (!simulation.session) {
                    return false
                }
                if (this.learner.id == simulation.session.learner1_id) {
                    let position = this.registration.degree.simulation_role_1_name[this.registration.degree.lang];
                    if (this.registration.degree.simulation_role_2) {
                        position += ' / ' + this.registration.degree.simulation_role_2_name[this.registration.degree.lang];
                    }
                    return position
                }
                if (this.learner.id == simulation.session.learner2_id) {
                    let position = this.registration.degree.simulation_role_2_name[this.registration.degree.lang];
                    if (this.registration.degree.simulation_role_1) {
                        position += ' / ' + this.registration.degree.simulation_role_1_name[this.registration.degree.lang];
                    }
                    return position
                }
                return false
            },

            openLog(simulation) {
                this.$router.push({
                    name: 'log-learner',
                    params: {
                        learnerId: this.$route.params.learnerId,
                        registrationId: this.$route.params.registrationId,
                        simulationId: simulation.id,
                    }
                });
            },

            openSessionLog(simulation) {
                this.$router.push({ name: 'log-session',
                    params: { 
                        sessionId: simulation.session.id,
                        registrationId: this.registration.id,
                        simulationId: simulation.id,
                        groupId: 0,
                    }
                })
            },

            openSimulation(simulation) {
                if (simulation.assessment && simulation.private) {
                    this.$router.push({
                        name: 'dashboard-learner-private-review-report',
                        params: {
                            learnerId: this.$route.params.learnerId,
                            registrationId: this.$route.params.registrationId,
                            simulationId: simulation.id,
                        }
                    });
                } else {
                    let routeName = simulation.state.published 
                        ? 'dashboard-learner-simulation'
                        : 'dashboard-learner-simulation-edit'

                    this.$router.push({
                        name: routeName,
                        params: {
                            learnerId: this.$route.params.learnerId,
                            registrationId: this.$route.params.registrationId,
                            simulationId: simulation.id,
                        }
                    });
                }
            },

            pinSimulation(simulation) {
                this.pinned = this.pinned == simulation.id ? null : simulation.id
                this.$auth.setUiConfig(this.pinned, 'pinned_simulation_in_registration_' + this.registration.id);
                this.$eventBus.$emit('simulation_pinned', this.pinned);
            },

            openComment(simulation) {
                this.comment = {
                    title: simulation.name,
                    content: simulation.state.comment,
                    author: simulation.state.instructor_name,
                }
                this.modals.open('comment')
            }
        }
    }
</script>

<style scoped>

    .card >>> td {
        padding: 1.5rem 0 1.5rem 1.5rem;
        font-size: 0.8rem;
    }
    .card >>> th.go-ground, .card >>> td.go-ground {
        padding-left: 0;
    }
    .card >>> td.actions {
        padding-right: 1.5rem;
    }
    .badge.badge-success {
        background-color: #1aae6f;
        color: #fff;
        font-size: 0.7rem;
    }
    .pin-icon {
        margin-left: 0;
        float: left !important;
        position: absolute;
        margin-top: 1px;
        cursor: pointer;
    }

</style>